import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ChatTextarea } from "../inputs/ChatTextarea";
import {
  StopGenerationButton,
  SubmitMessageButton,
  AttachFileButton,
} from "../buttons/ChatInputButtons";
import type { AttachedFile } from "./types";
import { FileChips } from "./FileChips";
import { FileDropZone } from "./FileDropZone";
import { StartupStatusBanner } from "./StartupStatusBanner";
import { usePasteImageAttachment } from "./usePasteImageAttachment";

const SendingSpinner = memo(function SendingSpinner({
  size,
}: {
  size?: "sm" | "md";
}) {
  const isSm = size === "sm";
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-900",
        isSm ? "mb-[8.5px] mr-2.5 h-7 w-7" : "h-8 w-8",
      )}
    >
      <Loader2 className={cn("animate-spin", isSm ? "h-3 w-3" : "h-3.5 w-3.5")} />
    </div>
  );
});

const SendOrStopButton = memo(function SendOrStopButton(props: {
  isRunning: boolean;
  isSending: boolean;
  onStop: () => void;
  disabled: boolean;
  size?: "sm" | "md";
}) {
  if (props.isSending) return <SendingSpinner size={props.size} />;
  if (props.isRunning) return <StopGenerationButton onClick={props.onStop} size={props.size} />;
  return <SubmitMessageButton disabled={props.disabled} size={props.size} />;
});

interface ChatInputProps {
  message: string;
  onMessageChange: (value: string) => void;
  attachedFiles: AttachedFile[];
  onAttachFiles: (files: FileList | File[]) => Promise<void>;
  onRemoveFile: (id: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onStop: () => void;
  recallLast: () => void;
  isRunning: boolean;
  isBlocked: boolean;
  isUploading: boolean;
  hasAttachmentErrors: boolean;
  isSending: boolean;
  startupStatus?: string | null;
  vmAvailable: boolean;
  mobileIsland?: ReactNode;
  selectionRef: React.MutableRefObject<{ start: number; end: number }>;
  className?: string;
}

export const ChatInput = memo(function ChatInput(props: ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mobileFormRef = useRef<HTMLFormElement>(null);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        props.onAttachFiles(e.target.files);
      }
      e.target.value = "";
    },
    [props.onAttachFiles],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      props.onSubmit(e);
      return;
    }
    // ArrowUp on an empty input restores the last submitted message —
    // single-step recall, especially handy if the previous send was
    // silently dropped by a guard and you just lost your text.
    if (e.key === "ArrowUp" && !props.message) {
      e.preventDefault();
      props.recallLast();
    }
  };

  const handlePaste = usePasteImageAttachment({
    vmAvailable: props.vmAvailable,
    onAttachFiles: props.onAttachFiles,
  });

  useEffect(() => {
    const form = mobileFormRef.current;
    if (!form) return;
    const prevent = (e: TouchEvent) => {
      e.preventDefault();
    };
    form.addEventListener("touchmove", prevent, { passive: false });
    return () => form.removeEventListener("touchmove", prevent);
  }, []);

  const submitDisabled =
    !props.message.trim() ||
    props.isBlocked ||
    props.isUploading ||
    props.hasAttachmentErrors;

  const startupBanner = props.startupStatus ? (
    <StartupStatusBanner status={props.startupStatus} />
  ) : null;

  const mobileInputContent = (
    <>
      <div className={props.vmAvailable ? undefined : "invisible"}>
        <AttachFileButton
          onClick={() => fileInputRef.current?.click()}
          disabled={props.isBlocked || !props.vmAvailable}
          size="sm"
        />
      </div>
      <ChatTextarea
        value={props.message}
        onChange={(e) => props.onMessageChange(e.target.value)}
        onPaste={handlePaste}
        disabled={props.isBlocked || props.isSending}
        autoFocus
        selectionRef={props.selectionRef}
        maxHeight={160}
        enterKeyHint="enter"
        autoComplete="off"
        className="min-h-[44px] min-w-0 flex-1 resize-none bg-transparent px-3 py-[13px] text-[16px] leading-[1.2] text-zinc-100 placeholder:text-zinc-600 focus:outline-none disabled:cursor-not-allowed"
      />
      <SendOrStopButton
        isRunning={props.isRunning}
        isSending={props.isSending}
        onStop={props.onStop}
        disabled={submitDisabled}
        size="sm"
      />
    </>
  );

  const desktopInputContent = (
    <>
      <ChatTextarea
        value={props.message}
        onChange={(e) => props.onMessageChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        disabled={props.isBlocked || props.isSending}
        autoFocus
        selectionRef={props.selectionRef}
      />
      {props.vmAvailable && (
        <FileChips
          files={props.attachedFiles}
          onRemove={props.onRemoveFile}
        />
      )}
      <div className={cn(
        "flex items-center px-2.5 pb-2.5",
        props.vmAvailable ? "justify-between" : "justify-end",
      )}>
        {props.vmAvailable && (
          <AttachFileButton
            onClick={() => fileInputRef.current?.click()}
            disabled={props.isBlocked}
          />
        )}
        <SendOrStopButton
          isRunning={props.isRunning}
          isSending={props.isSending}
          onStop={props.onStop}
          disabled={submitDisabled}
        />
      </div>
    </>
  );

  return (
    <div className={cn("w-full shrink-0 px-4", props.className)}>
      <div className="relative mx-auto max-w-2xl">
        {props.vmAvailable && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
        )}

        <div className="desktop:hidden">
          {props.vmAvailable && props.attachedFiles.length > 0 && (
            <div className="mb-2">
              <FileChips
                files={props.attachedFiles}
                onRemove={props.onRemoveFile}
              />
            </div>
          )}
          <div className="relative flex items-center gap-2">
            {props.mobileIsland}
            <form
              ref={mobileFormRef}
              onSubmit={props.onSubmit}
              className="min-w-0 flex-1"
            >
              {props.vmAvailable ? (
                <FileDropZone
                  onFiles={props.onAttachFiles}
                  disabled={props.isBlocked}
                  className="flex flex-col overflow-hidden rounded-2xl border backdrop-blur-xl transition-colors"
                >
                  {startupBanner}
                  <div className="flex items-end">{mobileInputContent}</div>
                </FileDropZone>
              ) : (
                <div className="flex flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-zinc-900/60 backdrop-blur-xl transition-colors focus-within:border-white/[0.1]">
                  {startupBanner}
                  <div className="flex items-end">{mobileInputContent}</div>
                </div>
              )}
            </form>
          </div>
        </div>

        <form onSubmit={props.onSubmit} className="hidden desktop:block">
          {props.vmAvailable ? (
            <FileDropZone
              onFiles={props.onAttachFiles}
              disabled={props.isBlocked}
              className="overflow-hidden rounded-2xl border backdrop-blur-xl transition-colors"
            >
              {startupBanner}
              {desktopInputContent}
            </FileDropZone>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-zinc-900/60 backdrop-blur-xl transition-colors focus-within:border-white/[0.1]">
              {startupBanner}
              {desktopInputContent}
            </div>
          )}
        </form>
      </div>
    </div>
  );
});
